import { findLowestAnchorRoom, roomPriceChangePercent } from './PriceRecommendationDrawerContents.utilities';

// The kinds of day of the Day Pricing drawer (ROO2-313 R2): no state, only held states, only Room order lifts, or a
// recommendation.
export type DayKind = 'none' | 'held' | 'roomOrder' | 'valid';

type DayState = { isValid: boolean, rankAdjusted: boolean };

// A Room order lift: a valid change that the pricer made only to keep a dearer room type above a cheaper one.
export const isRoomOrderLift = (state: DayState): boolean => state.isValid && state.rankAdjusted;

export const findDayKind = (states: readonly DayState[]): DayKind => {
	if (states.length === 0) return 'none';
	const valid = states.filter(state => state.isValid);
	if (valid.length === 0) return 'held';
	return valid.every(isRoomOrderLift) ? 'roomOrder' : 'valid';
};

// The room type just below in the room order, among the room types of the day: the type a Room order lift stays above.
export const findLowerRoomType = <Type extends string>(
	state: { roomMappingType: Type },
	dayStates: readonly { roomMappingType: Type }[],
	order: readonly Type[],
): Type | undefined => {
	const rank = (type: Type) => order.indexOf(type);
	return dayStates.map(other => other.roomMappingType)
		.filter(type => rank(type) < rank(state.roomMappingType))
		.sort((a, b) => rank(b) - rank(a))[0];
};

// The percent of the Recommended change pill, as a rate: the Change of the room type's master room (the value of the
// table's Change column); the engine change when the state has no master room.
export const recommendedChangeRate = (state: {
	minimumBasePriceRoomStates: readonly { isAnchor: boolean, basePrice: number, finalPrice: number }[] | null | undefined,
	effectiveChangeRate: number,
}): number => {
	const master = findLowestAnchorRoom(state.minimumBasePriceRoomStates?.map(room => ({ ...room, changeRate: 0 })));
	if (master === undefined) return state.effectiveChangeRate;
	return roomPriceChangePercent(master.basePrice, master.finalPrice) / 100;
};
